import { useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  Session,
  useCurrenciesMap,
  useCurrencyOptions,
  useIncentiveForSession,
  useTranslateIncentiveText,
  useUpdateIncentiveInPlan,
} from '@app/api';
import { Input, Select } from '@app/form';
import { getCurrencyAmountAsFloat, getRoundedUpEuro } from 'lib/conversions';
import { Button, InformationBox, useToast } from 'components';
import { Trans, useTranslation } from 'react-i18next';
import { useIncentiveEditionControllingState } from 'hooks/useIncentiveEditionControllingState';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    gap: theme.marginBase,
    flexWrap: 'wrap',
  },
  amount: {
    flex: 2,
    minWidth: 112,
  },
  currency: {
    flex: 1,
    minWidth: 112,
  },
  preview: {
    flex: 4,
    minWidth: 240,
  },
  amountInput: {
    '-moz-appearance': 'textfield',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  box: {
    marginTop: theme.marginBase * 2,
  },
  strong: {
    color: theme.colors.blueAccessible,
  },
  fees: {
    color: theme.colors.greenAccessible,
  },
  bg: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

interface Props {
  session: Session;
}

export const EditablePlanIncentive = ({ session }: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { t } = useTranslation();

  const { mutateAsync: updateIncentive, isLoading } = useUpdateIncentiveInPlan(
    session.planId,
  );

  const { data: mainIncentive } = useIncentiveForSession(session.id);

  const { incentive, onCurrencyChange, onAmountChange } =
    useIncentiveEditionControllingState(mainIncentive);

  const currenciesOptions = useCurrencyOptions();
  const currencyMap = useCurrenciesMap();

  const amount = incentive.amount;

  const currencyInfo = currencyMap[incentive.currencyCode];
  const decimals =
    currencyInfo?.decimal === undefined ? 2 : currencyInfo.decimal;

  const eurAmount = useMemo(() => {
    if (!currencyInfo) {
      return undefined;
    }
    return getRoundedUpEuro(amount, currencyInfo);
  }, [amount, currencyInfo]);

  const onSubmit = useCallback(async () => {
    return updateIncentive(incentive, {
      onSuccess: () => toast.saved(),
      onError: (e) => toast.exception(e),
    });
  }, [toast, incentive, updateIncentive]);

  const text = useTranslateIncentiveText(incentive, session.locale, session);

  return (
    <div className={classes.bg}>
      <div className={classes.container}>
        <Input
          label={t('editSession.public.incentive.amount')}
          value={getCurrencyAmountAsFloat(amount, decimals)}
          noMargin
          containerClass={classes.amount}
          className={classes.amountInput}
          onChange={onAmountChange}
          type="number"
        />
        <Select
          label={t('editSession.public.incentive.currency')}
          value={incentive.currencyCode}
          options={currenciesOptions}
          noMargin
          containerClass={classes.currency}
          onChange={onCurrencyChange}
        />
        <Input
          label={t('editSession.public.incentive.displayed')}
          value={text}
          noMargin
          disabled
          containerClass={classes.preview}
        />
      </div>

      {incentive.currencyCode !== 'EUR' && (
        <InformationBox className={classes.box}>
          <Trans
            i18nKey="editSession.public.incentive.roundMessage"
            components={{
              strong: <strong className={classes.strong} />,
              fees: <strong className={classes.fees} />,
            }}
            values={{
              amount: eurAmount,
            }}
          />
        </InformationBox>
      )}
      <div className={classes.buttonContainer}>
        <Button
          type="button"
          variant="transparent"
          text={t('editSession.public.incentive.apply')}
          onClick={onSubmit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
